import { useEffect, useState } from "react";
import { api, ACTING_KEY, formatApiErrorDetail } from "@/lib/api";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { ReceiptLimitControl } from "@/components/admin/ReceiptLimitControl";

export const SetupPanel = ({ currentId }) => {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/platform/restaurants").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  const toggle = async (row) => {
    try {
      await api.put(`/platform/restaurants/${row.id}/loyalty`, { enabled: !row.loyalty_enabled });
      setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, loyalty_enabled: !row.loyalty_enabled } : r)));
      toast.success(`Loyalty ${row.loyalty_enabled ? "disabled" : "enabled"} for ${row.name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const open = (id) => {
    localStorage.setItem(ACTING_KEY, id);
    window.location.reload();
  };

  if (!rows) return <Loader2 className="h-6 w-6 animate-spin text-orange-500" />;

  return (
    <div data-testid="setup-panel" className="space-y-6">
      <div>
        <h2 className="font-heading text-3xl font-bold flex items-center gap-2"><ShieldCheck className="h-7 w-7 text-orange-400" /> Setup</h2>
        <p className="text-slate-400 text-sm mt-1">Platform admin only. Turn the digital stamp loyalty program on or off, set how many email receipts each restaurant can send per month, or open any restaurant's dashboard.</p>
      </div>
      <div className="bg-slate-900 border border-slate-800 rounded-2xl divide-y divide-slate-800">
        {rows.map((r) => (
          <div key={r.id} data-testid={`setup-row-${r.id}`} className="flex flex-wrap items-center gap-4 p-4">
            <div className="flex-1 min-w-[180px]">
              <p className="font-semibold">{r.name} {r.id === currentId && <span className="ml-1 text-[10px] uppercase tracking-wider text-orange-400">Viewing</span>}</p>
              <p className="text-xs text-slate-500">{r.owner_email || "No owner"}</p>
            </div>
            <button data-testid={`setup-loyalty-toggle-${r.id}`} onClick={() => toggle(r)} className="flex items-center gap-2 text-sm">
              <span className={r.loyalty_enabled ? "text-emerald-400" : "text-slate-400"}>Stamp loyalty {r.loyalty_enabled ? "on" : "off"}</span>
              <span className={`relative h-6 w-11 rounded-full transition-colors ${r.loyalty_enabled ? "bg-emerald-600" : "bg-slate-600"}`}>
                <span className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${r.loyalty_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </span>
            </button>
            <ReceiptLimitControl row={r} onSaved={(limit) => setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, receipt_limit: limit } : x)))} />
            <button data-testid={`setup-open-${r.id}`} onClick={() => open(r.id)} disabled={r.id === currentId} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold bg-slate-800 hover:bg-slate-700 disabled:opacity-40 transition-colors">
              Open dashboard <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
